import { z } from 'zod';

import { PreviewTypeSchema } from './preview-schemas.js';

/**
 * Schema for data attachment types (destination, hotel, place).
 *
 * Data attachments are the subset of {@link PreviewTypeSchema} that can be
 * attached to a chat message as a standalone reference card. `flight` and
 * `room` are valid preview types but live under a trip (not standalone
 * entities), so they are intentionally excluded here. Derived from the
 * canonical preview vocabulary via `.extract()` so the two cannot drift.
 */
export const DataAttachmentTypeSchema = PreviewTypeSchema.extract(['destination', 'hotel', 'place']);

/**
 * Type for data attachment types
 */
export type DataAttachmentType = z.infer<typeof DataAttachmentTypeSchema>;

/**
 * Generic file attachment with metadata.
 * Used for booking attachments (receipts, confirmations, etc.) and reusable
 * by any future feature that needs file attachments with stored metadata.
 */
export const FileAttachmentSchema = z.object({
  url: z.string(),
  name: z.string(),
  mimeType: z.string().optional(),
  size: z.number().optional(),
});
export type FileAttachment = z.infer<typeof FileAttachmentSchema>;

/**
 * Normalizes a raw URL string (legacy format) or structured FileAttachment.
 * Used for backward compatibility with existing Firestore documents that store
 * attachments as plain URL strings.
 */
export function normalizeFileAttachment(a: string | FileAttachment): FileAttachment {
  if (typeof a === 'string') {
    let name: string;
    try {
      name = decodeURIComponent(a.split('?')[0] ?? '').split('/').pop() ?? 'Attachment';
    } catch {
      name = (a.split('/').pop() ?? '').split('?')[0] ?? 'Attachment';
    }
    return { url: a, name };
  }
  return a;
}

/** Union schema that accepts both legacy string URLs and structured FileAttachment objects. */
export const FileAttachmentOrStringSchema = z.union([z.string(), FileAttachmentSchema]);
